import {
 useEffect, useRef, useState
} from "react";
import {
 useNavigate, useSearchParams
} from "react-router-dom";
import {
 useAuth
} from "../hooks/useAuth";
import {
 authService,
 billingService
} from "../services/api";
import {
 AlertTriangle
} from 'lucide-react';
import {
 identifyUser,
 OnboardingEvents,
 OnboardingWizardEvents,
 trackEvent,
 trackReferralSignupCompleted
} from '../analytics/posthog';
import {
 trackBeehiivSignup
} from '../analytics/beehiiv';
import {
 trackOpenAiRegistration
} from '../analytics/openaiPixel';
import {
 clearStoredReferralCode,
 getStoredReferralCode,
 getValidStoredReferralCode,
 hasCheckoutIntent,
 referralCodeIsSpent
} from '../utils/referral';
import {
 hasPortalIntent
} from '../utils/billing';
import {
 integrationConnectPath, isViewPath, returnPathForLoginSource, settingsSectionPath, viewPath
} from '../utils/routes';
import {
 clearPendingRoute, peekPendingRoute, samePage
} from '../utils/pendingRoute';
import {
  applyStoredPresignupAssistant
} from '../utils/presignupAssistant';

// The onboarding wizard's own path, as both permissions steps hand it to
// scopeService.requestScopes. Matched on the pathname so a return path that
// grows a query string still counts, and deliberately narrow: the same grant
// made from Settings returns to /integrations and from the referral page to
// /refer, and neither is a step of the onboarding funnel.
//
// Note the coupling this inherits from the backend: `request_scopes` coerces
// any return path outside SCOPE_UPGRADE_ALLOWED_RETURN_PATHS to `/onboarding`
// (auth.py, the open-redirect guard). Latent today — all five call sites pass
// an allowlisted path — but a new surface passing an unlisted one would have
// its grants counted as onboarding grants, which reads as real funnel
// movement. Add the path to that allowlist when you add a surface.
const ONBOARDING_PATH = "/onboarding";

function isOnboardingReturnPath(returnPath: string | null): boolean {
  if (!returnPath) return false;
  try {
    return (
      new URL(returnPath, window.location.origin).pathname === ONBOARDING_PATH
    );
  } catch {
    // A return path this malformed is the routing block's problem, not the
    // funnel's — an analytics gate must not be what fails a real sign-in.
    return false;
  }
}

export function CallbackPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // One-shot: a sign-in callback must be handled exactly once per mount. The
  // effect below both calls `setAuthUser` and depends on it, so before the
  // context was memoized every run scheduled the next one and the whole
  // handler — analytics included — replayed 2-3x per real sign-in
  // (GET-10278). The memoization is the fix; this guard is what keeps any
  // future dependency churn (or StrictMode's double-invoke in dev) from
  // re-emitting the funnel events or re-running the identify/navigate work.
  const callbackHandled = useRef(false);

  useEffect(() => {
    const handleCallback = async (): Promise<void> => {
      // A resumed replay of an authorization code already exchanged by an
      // earlier pass — the backend sends `?resumed=1` rather than the usual
      // callback params (GET-14095). The sign-in it belongs to already
      // happened and already fired these, and the ref above only dedupes
      // within one mount, so firing them again would put two
      // `sign_in_completed`s in the funnel for one sign-in. Everything else on
      // this page still runs: the session is real and the user is being
      // routed into the app exactly as the first pass intended.
      const isResumedCallback = searchParams.get("resumed") === "1";

      if (!isResumedCallback) {
        trackEvent(OnboardingEvents.OAUTH_CALLBACK_RECEIVED);
      }

      const errorParam = searchParams.get("error");

      if (errorParam) {
        trackEvent(OnboardingEvents.SIGN_IN_FAILED, { error: errorParam });
        setError(`Authentication failed: ${errorParam}`);
        return;
      }

      try {
        // Token is already set as httponly cookie by the backend
        // Just fetch the user data
        // A pick made on /login before signup (GET-18726) is saved before
        // anything routes on `selected_character_id`.
        const user = await applyStoredPresignupAssistant(
          await authService.getCurrentUser(),
        );
        setAuthUser(user);

        // Identify user in PostHog for funnel tracking. Deliberately NOT
        // gated on `isResumedCallback`: this is not a funnel step, and a
        // resume may be the first mount that ever learns the user id, so
        // skipping it would lose the anon-to-user stitch rather than avoid a
        // double-count.
        // Split name into firstName/lastName for LinkedIn Ads Conversions destination
        const nameParts = (user.name || '').trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
        identifyUser(String(user.id), {
          email: user.email,
          name: user.name,
          firstName,
          lastName,
        });
        if (!isResumedCallback) {
          trackEvent(OnboardingEvents.SIGN_IN_COMPLETED);
        }

        // Where a scope upgrade asked to be returned to, and whether some of
        // what it asked for came back unchecked. Read here rather than at the
        // routing block below because the onboarding permissions event needs
        // them too.
        const returnPath = searchParams.get("return_path");
        const scopeGrantIncomplete
          = searchParams.get("scope_grant_incomplete") === "1";

        // The server fires GrantedMailCalendar to Meta during the OAuth callback
        // — before this page loads and before sign_in_completed — so a
        // server-side PostHog copy would land before step 3 and the ordered
        // acquisition funnel's optional GrantedMailCalendar step would never
        // match. The backend instead sets `granted_mail_calendar=1` exactly when
        // the mail/calendar cluster was granted in this round (Microsoft signup
        // or a Google scope-upgrade), so we emit the single PostHog copy here,
        // after sign_in_completed.
        if (searchParams.get("granted_mail_calendar") === "1") {
          trackEvent(OnboardingEvents.GRANTED_MAIL_CALENDAR);

          // The wizard's permissions step ends in a full-page redirect to
          // Google (scopeService.requestScopes assigns window.location.href),
          // so the step unmounts and OnboardingPage's own PERMISSIONS_GRANTED
          // call never runs for the user who just granted — 2 events in 90 days
          // against 1,628 grant clicks. The grant completing is observable only
          // here, on the way back, which is why it is emitted from the callback
          // rather than from the step.
          //
          // Gated on the same `granted_mail_calendar=1` as the copy above, so it
          // inherits that param's guarantees rather than restating them: the
          // backend sets it only on the real mail/calendar TRANSITION
          // (`ScopeGrantResult.mail_calendar_newly_granted`), so re-granting
          // does not fire a second one; someone who abandons at Google's consent
          // screen never reaches this page at all; a partial grant that left
          // mail/calendar unchecked arrives without it and with
          // `scope_grant_incomplete=1`, which the step reports as
          // PERMISSIONS_INCOMPLETE; and a resumed replay (`resumed=1`) carries
          // none of the one-shot params.
          //
          // `clusters` is what the step ASKED for, matching
          // PERMISSIONS_GRANT_CLICKED, so it is not a granted-set: a return can
          // carry `granted_mail_calendar=1` and `scope_grant_incomplete=1`
          // together when mail/calendar came back but Meet did not. `partial`
          // is what says so, and without it the list reads as the grant.
          //
          // No `variant`: the rest of the permissions events tag the V2 arm
          // with `connect_v2`, and this one cannot — the callback does not know
          // which step sent the user. PostHog's automatic `$feature/*`
          // properties keep the experiment analysable, so the gap is deliberate
          // rather than an oversight to rediscover during analysis.
          if (isOnboardingReturnPath(returnPath)) {
            const requestedClusters = searchParams.get("requested_clusters");
            trackEvent(OnboardingWizardEvents.PERMISSIONS_GRANTED, {
              ...(requestedClusters
                ? { clusters: requestedClusters.split(",") }
                : {}),
              partial: scopeGrantIncomplete,
            });
          }
        }

        // Backend-provided genuine-signup signals (set as callback params in
        // auth.py). `is_new_user` is one-shot — set only on the account-creating
        // callback — whereas `reactivated` is set on every login until setup
        // completes.
        const isNewSignup = searchParams.get("is_new_user") === "1";
        const isReactivation = searchParams.get("reactivated") === "1";

        // Referral funnel: record the conversion step only on a genuine new
        // signup, keyed to the (now-identified) referred user. The stored
        // referral code persists across logins (cleared only after subscribing),
        // so gating on the one-shot `is_new_user` signal — rather than mere code
        // presence — keeps REFERRAL_SIGNUP_COMPLETED firing exactly once at real
        // signup instead of re-firing on every later login. Reactivations are
        // intentionally excluded: a referral conversion is a new-user
        // acquisition, and `reactivated` is not one-shot (counting it would
        // reintroduce double-counting and need a per-user dedup guard the
        // new-signup gate avoids).
        const referralCode = getStoredReferralCode();
        if (isNewSignup && referralCode) {
          trackReferralSignupCompleted(referralCode);
        }

        // Existing-subscriber cleanup. The referral code persists in
        // localStorage to survive the OAuth round-trip and apply the
        // first-month discount at the referred user's first checkout. Once the
        // code can no longer be spent — the user has a Stripe subscription of
        // their own — drop it here so the "A friend has you covered" signup
        // banner stops resurfacing on every later login. Gated on a stored code
        // so the billing lookup is skipped for the common case, and best-effort
        // so a billing hiccup never blocks sign-in.
        //
        // `referralCodeIsSpent` owns that test, and mirrors the backend's. It
        // must NOT be relaxed back to `is_active`: this same callback is the
        // one that grants a business signup their cardless trial, which is
        // active but has never been near checkout, so clearing on it destroyed
        // the code minutes before the onboarding billing step asked for it
        // (GET-12622).
        if (getValidStoredReferralCode()) {
          try {
            const subscription = await billingService.getSubscription();
            if (
              referralCodeIsSpent({
                stripeSubscriptionId: subscription.stripe_subscription_id,
              })
            ) {
              clearStoredReferralCode();
            }
          } catch (subscriptionErr) {
            console.error(
              "Subscription status check for referral cleanup failed:",
              subscriptionErr
            );
          }
        }

        // Newsletter ad → signup conversion. Fires for a genuine new signup
        // (`is_new_user`) and also when a previously deactivated user signs back
        // in (`reactivated`, set by the backend when an active returning user has
        // no completed setup). Both gated to company emails (`!is_public_email`),
        // mirroring Meta's `CompleteRegistrationBusiness`, which targets the
        // higher-value corporate cohort and skips public/free-domain signups. The
        // helper adds beehiiv's `_bhc` attribution + marketing-consent gate and
        // dedupes to one conversion per user — the `reactivated` signal is not
        // one-shot (set on every login until setup completes), so the per-user
        // guard prevents double-counting. `is_public_email` is computed server-side
        // from the same domain list the backend uses for that Meta split.
        if ((isNewSignup || isReactivation) && !user.is_public_email) {
          trackBeehiivSignup(String(user.id));
        }

        // ChatGPT ads `registration_completed`, browser half. New signups only
        // and company emails only, exactly the cohort the server-side copy
        // (post_sign_in_fanout → openai_ads_conversions) sends, with the same
        // event id so OpenAI keeps one. Not awaited: it hashes the email
        // first, and sign-in must not wait on an ad pixel.
        if (isNewSignup && !user.is_public_email) {
          void trackOpenAiRegistration(String(user.id), user.email);
        }

        // Check if this is a scope upgrade callback with a return path
        const scopeUpgrade = searchParams.get("scope_upgrade");
        const requestedClusters = searchParams.get("requested_clusters");
        if (returnPath) {
          const returnUrl = new URL(returnPath, window.location.origin);
          if (scopeGrantIncomplete) {
            returnUrl.searchParams.set("scope_grant_incomplete", "1");
          }
          const destination = `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
          navigate(destination, {
            state: scopeUpgrade
              ? { scopeUpgrade: true, requestedClusters }
              : undefined,
          });
        } else {
          // Which shared link (if any) started this sign-in. Round-tripped
          // through the OAuth `state` and echoed back as a callback param, so
          // it survives even if sessionStorage is lost; we fall back to the
          // login_params stashed on the login page for same-tab flows. It
          // carries the destination, not just "a link was followed" — the
          // ambassador page, for one, is a query param that would otherwise be
          // lost across the hop.
          const loginParams = new URLSearchParams(
            sessionStorage.getItem("login_params") ?? "",
          );
          const source = searchParams.get("source") ?? loginParams.get("source");
          const needsOnboarding = user.setup_completed_at == null;

          // Shared app links (the assistant's "add another inbox", a billing
          // email's CTA, a referral entry) send a signed-out user through their
          // provider and back here, so resume where the link was going instead
          // of dropping them on the dashboard to find the page themselves — the
          // detour is what got them signing in with the wrong address in the
          // first place (GET-9017). One table, so a new destination is a row
          // rather than another branch here.
          const destination = returnPathForLoginSource(source);
          // Which integration the connect link that started this was for,
          // round-tripped through the OAuth `state` beside `source`. Only a
          // `/go` link carries one.
          const connectSlug = searchParams.get("connect");
          const connectPath = connectSlug
            ? integrationConnectPath(connectSlug, searchParams.get("connect_name"))
            : null;
          // Which assistant view a view link was opening (GET-15593), carried
          // the same way: the OAuth `state` for Google, a state-bound cookie for
          // Microsoft, and the stashed login params for any other sign-in.
          const openedView = viewPath(
            searchParams.get("view") ?? loginParams.get("view"),
          );
          // Which Settings section a section link was opening (GET-18882),
          // carried exactly as the view is.
          const openedSection = settingsSectionPath(
            searchParams.get("section") ?? loginParams.get("section"),
          );
          // Where this sign-in says it was going, before anything remembered.
          const named =
            openedView ?? connectPath ?? openedSection ?? destination?.path ?? null;

          // The URL an auth guard turned them away from, if they were turned
          // away from one. It beats `named` because `source` is a bare slug and
          // this still has the query string — which is the whole of what a deep
          // link asks for (GET-13956) — but only for the same page: the stash
          // is session-scoped, so an abandoned bounce earlier in the same tab
          // leaves one behind, and letting that win would send a later
          // `/go?dest=checkout` recipient somewhere the link never named. A
          // rejected one is DROPPED rather than merely ignored, because the
          // onboarding wizard's exit resumes whatever is left and would
          // misroute the same visitor one hop later. `bypassOnboarding` stays
          // the destination table's call either way: a remembered URL says
          // where, never whether setup may be skipped.
          const pending = peekPendingRoute();
          const remembered = pending && samePage(pending, named) ? pending : null;
          if (pending && !remembered) clearPendingRoute();

          const resume = remembered ?? named;
          // A view opens before setup is finished however the sign-in found it,
          // since its route sits outside the onboarding guard anyway.
          const bypassOnboarding =
            destination?.bypassOnboarding ??
            (resume !== null && isViewPath(resume.split("?")[0] ?? ""));
          if (resume && (bypassOnboarding || !needsOnboarding)) {
            sessionStorage.removeItem("login_params");
            clearPendingRoute();
            navigate(resume, { replace: true });
            return;
          }

          // Honor a dunning email's card-update deep link: send the user back
          // to pricing where the Stripe portal auto-opens (utils/billing.ts).
          // Checked before the checkout intent — a broken payment outranks a
          // new purchase.
          if (!needsOnboarding && hasPortalIntent()) {
            navigate("/pricing?portal=true");
            return;
          }

          // Honor a referral / checkout intent for already-onboarded users:
          // send them to pricing where checkout auto-opens with the discount.
          // New users onboard first; their stored referral code still applies
          // at their first checkout.
          if (!needsOnboarding && hasCheckoutIntent()) {
            navigate("/pricing?checkout=true");
            return;
          }

          navigate(needsOnboarding ? "/onboarding" : "/integrations");
        }
      } catch (err) {
        console.error("Authentication failed:", err);
        trackEvent(OnboardingEvents.SIGN_IN_FAILED, { error: 'fetch_user_failed' });
        setError("Authentication failed. Please try again.");
      }
    };

    if (callbackHandled.current) return;
    callbackHandled.current = true;
    void handleCallback();
  }, [searchParams, navigate, setAuthUser]);

  if (error) {
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <AlertTriangle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Authentication Failed
            </h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate("/login")}
              className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Completing Sign In...
          </h1>
          <p className="text-gray-600">
            Please wait while we complete your authentication.
          </p>
        </div>
      </div>
    </div>
  );
}
